/**
 * PHYSICAL-RISK.FORMATTER.TS
 * Format risk object
 */
import { Session } from "@/redux/_session.types"
import { PhysicalRisk } from "./physical-risk.types"
import { PhysicalRiskContext } from "./physical-risk.store"
import { getMaxEvaluation, getPopulationEvaluation, updateEvaluation } from "./physical-risk.utils"
import { getRiskResidualUi } from "@/entities/physical-risk/model/account-risks.utils"
import { getScale, normalizeRiskAttributes, AccountRisksNormalizedValue } from "@/redux/account.types"
import { sum } from "lodash"

function getRiskNormalizedValue(
  riskIndex: number,
  valueEffective: number,
  cNormalizedValues: AccountRisksNormalizedValue[][] = []
) {
  const levelValues = cNormalizedValues[riskIndex]
  if (!Array.isArray(levelValues)) return null
  return levelValues.find((item) => item.value === valueEffective)?.normalizedValue ?? null
}

function normalizeDate(value: Date | string | null) {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Formatter function
 * populationId : score vu depuis cette fonction ; sinon celui de la fonction la plus exposee.
 * Gravite et frequence du risque restent les siennes (celles que l'on edite).
 */
export function formatPhysicalRisk(
  risk: PhysicalRisk,
  _session: Session,
  context: PhysicalRiskContext,
  populationId?: string
): PhysicalRisk {
  const { severityScale, frequencyScale, preventionScale, riskAttributes, residualRiskScale, residualRiskLevels, cNormalizedValues } =
    context.accountRisksSettings
  const populationIds = risk.populationIds ?? []
  const siteIds = risk.siteIds ?? []

  //Get family
  const cFamily = context.riskFamilies.find((x) => x.id === risk.RiskFamilyId) ?? null

  const sScale = getScale(severityScale)
  const fScale = getScale(frequencyScale)
  const pScale = getScale(preventionScale)

  //Get size
  const cSize = sum(context.populations.filter((x) => populationIds.includes(x.id) && x.size).map((x) => x.size)) ?? 0

  //Format attributes : les valeurs sont indexees par id d'attribut, les libelles viennent des
  //reglages. cAttributes porte les deux, dans l'ordre des reglages, pour tout l'affichage.
  const settingsAttributes = normalizeRiskAttributes(riskAttributes)
  const attributes: Record<string, string> = {}
  const cAttributes = settingsAttributes.map((attribute) => {
    const value = risk.attributes?.[attribute.id] ?? ""
    attributes[attribute.id] = value
    return { ...attribute, value }
  })

  // Compute UI names from first attributes
  const cName = cAttributes[0]?.value ?? ""

  //Evaluation qui donne le score
  const evaluation = populationId ? getPopulationEvaluation(risk, populationId) : getMaxEvaluation(risk)

  // Normalize values + get labels (use merged scales)
  const frequencyEvaluation = updateEvaluation(evaluation.frequency, fScale)
  const cFrequencyLabel = frequencyEvaluation.label
  const frequency = updateEvaluation(risk.frequency, fScale).value

  //Idem for prevention
  const preventionEvaluation = updateEvaluation(risk.prevention, pScale)
  const cPreventionLabel = preventionEvaluation.label
  const prevention = preventionEvaluation.value

  //Idem pour severity
  const severityEvaluation = updateEvaluation(evaluation.severity, sScale)
  const cSeverityLabel = severityEvaluation.label
  const severity = updateEvaluation(risk.severity, sScale).value

  //Calculate value initial
  const cValueInitial = frequencyEvaluation.value * severityEvaluation.value
  const { color: cValueInitialColor, label: cValueInitialLabel } = getRiskResidualUi(cValueInitial, residualRiskScale, residualRiskLevels)

  //Calculate value effective
  const cValueEffective = cValueInitial / Math.max(1, prevention)
  const {
    color: cValueEffectiveColor,
    label: cValueEffectiveLabel,
    index: cValueEffectiveIndex
  } = getRiskResidualUi(cValueEffective, residualRiskScale, residualRiskLevels)

  //Calculate value normalize
  const cValueNormalized = getRiskNormalizedValue(cValueEffectiveIndex, cValueEffective, cNormalizedValues)

  return {
    ...risk,
    frequency,
    severity,
    prevention,
    updatedAt: normalizeDate(risk.updatedAt),
    attributes,
    populationIds,
    populationEvaluations: risk.populationEvaluations ?? [],
    siteIds,

    //Calculated
    cAttributes,
    cFamily,
    cSize,
    cName,
    cFrequencyLabel,
    cPreventionLabel,
    cSeverityLabel,
    cFrequencyScale: fScale,
    cPreventionScale: pScale,
    cSeverityScale: sScale,
    cValueEffective,
    cValueEffectiveColor,
    cValueEffectiveIndex,
    cValueEffectiveLabel,
    cValueNormalized,
    cValueInitial,
    cValueInitialColor,
    cValueInitialLabel
  }
}

//Risques d'une fonction, avec le score vu depuis cette fonction
export function formatPhysicalRisksForPopulation(
  risks: PhysicalRisk[],
  context: PhysicalRiskContext,
  populationId: string
): PhysicalRisk[] {
  return risks
    .filter((risk) => risk.populationIds.includes(populationId))
    .map((risk) => formatPhysicalRisk(risk, undefined as unknown as Session, context, populationId))
}
